import * as ko from "knockout";
import TabPage from "../factories/tab-page";
import Bao from "../services/bao-service";
import BaoDialog from "../factories/bao-dialog";
import "../bindings/bao-searcher";
import ConfigurationItemBaseViewModel from "./configuration-item-base";
import { ApplicationIds } from "../../core/application-ids";
import StartAuditDialog from "./dialogs/start-audit-dialog";

class AuditDefinition extends ConfigurationItemBaseViewModel {

    constructor(tabPage: TabPage) {
        super(tabPage);
        this.SettingsKeyPermission("AuditDefinitionEdit");
        this.SettingsKeyPermissionApplicationId(ApplicationIds.Checkware);
        this.AuditTrailKeyPermission("AuditDefinitionAuditTrail");
    }

    /**
     * Foreign key Id of Checklist(template).
     */
    public ChecklistId: KnockoutObservable<string> = ko.observable<string>();

    /**
     * Name of Checklist(template).
     */
    public ChecklistName: KnockoutObservable<string> = ko.observable<string>();

    /**
     * Foreign key Id of WorkItemCollection.
     */
    public WorkItemCollectionId: KnockoutObservable<string> = ko.observable<string>();

    /**
     * Name of WorkItemCollection.
     */
    public WorkItemCollectionName: KnockoutObservable<string> = ko.observable<string>();

    /**
     * Foreign key Id of WorkItemCollection.
     */
    public FolderStructureChecklistId: KnockoutObservable<string> = ko.observable<string>();

    /**
     * Name of WorkItemCollection.
     */
    public FolderStructureChecklistName: KnockoutObservable<string> = ko.observable<string>();

    /**
     * Permission key for opening the audit trail.
     */
    public PermissionAuditTrail: KnockoutObservable<string> = ko.observable(null);

    /**
     * Permission key for starting an Audit.
     */
    public PermissionStartAudit: KnockoutObservable<string> = ko.observable("AuditDefinitionStartAudit");

     /**
     * Permission key for navigating backwards (from search results).
     */
    public PermissionForward: KnockoutObservable<string> = ko.observable<string>(null);

    /**
     *  Permission key for navigating forward (from search results).
     */
    public PermissionBackward: KnockoutObservable<string> = ko.observable<string>(null);

    /**
     * Bao-Searcher for selection of workitemcollection.
     */    
    public SearcherWorkItemCollection: Bindings.baoSearcher = {
        SearchType: 'ShiftBook',
        ObjectKey: 'WorkItemCollectionId',
        ObjectId: this.WorkItemCollectionId,
        ObjectValue: 'WorkItemCollectionName',
        AutoSearch: true,
        AutoComplete: true,
        CustomFilter: { ShiftbookTypeId: 'C137822F-507D-4DF2-B201-45E968EBD636' }
    }

    /**
     * Bao-Searcher for selection of workitemcollection.
     */
    public SearcherFolderStructureChecklist: Bindings.baoSearcher = {
        SearchType: 'FolderStructureChecklist',
        ObjectId: this.FolderStructureChecklistId,
        ObjectValue: 'ChecklistName',
        AutoSearch: true,
        AutoComplete: true
    }


    /**
     * The init url for the backend.
     */
    public InitUrl(): string {
        return "Auditdefinition/Init";
    }

    /**
     * Calls the init() function from base class.
     */
    public init(): JQueryPromise<void> {
        return super.init();
    }

    /**
     * Checks if all mandatory fields are filled and prevents from saving if so.
     * @returns True if all mandatory fields are filled.
     */
    public CompleteCheck(): boolean | JQueryPromise<boolean> {
        let completed: boolean = true;

        // check Name and ChecklistId
        if (!this.Name1() || !this.FolderStructureChecklistId() || !this.WorkItemCollectionId()) {
            completed = false;
        }
        // show message to fill mandatory fields.
        if (!completed) {
            new BaoDialog(Bao.Session.ApplicationName, Bao.Session.GetMessage("msgFillMandatorFields"), "info");
            return false;
        }
        return true;
    }

    /**
     * Is called when menu button other than save, refresh, delete, qr-code is clicked. 
     * @param key Action key from a menu button.
     */
    public CustomToolbarClick(key: string) {
        switch (key) {
            case this.PermissionStartAudit():
                this.StartAudit();
                break;
        }
    }

    /**
     * Starts the Audit manually.
     */
    protected StartAudit() {
        this.CheckAndSave().then(() => 
        {
            Bao.post("AuditDefinition/StartAuditDialog", JSON.stringify({}), (result) => {
                const dialog = new BaoDialog(Bao.Session.ApplicationName);
                dialog.ViewModel(new StartAuditDialog(dialog));
                const okButton = new BaoDialog.ButtonFactory(dialog, Bao.Session.GetMessage("msgOk"), () => {
                    if (dialog.ViewModel().CompleteCheck()) {
                        this.TabPage.IsLoading(true)
                        Bao.post("AuditDefinition/StartAudit", JSON.stringify({
                            auditDefinitionId: ko.unwrap(this.ConfigurationItemId),
                            startDate: ko.unwrap(dialog.ViewModel().StartDate),
                            endDate: ko.unwrap(dialog.ViewModel().EndDate)
                        }), () => {
                            dialog.Close();
                            this.TabPage.IsLoading(false)
                            this.PerformRefresh();
                        });
                    } else {
                        new BaoDialog(Bao.Session.ApplicationName, Bao.Session.GetMessage("msgFillMandatorFields"), "info");
                    }
                });
                const cancelButton = new BaoDialog.ButtonFactory(dialog, Bao.Session.GetMessage("msgCancel"), () => {
                    dialog.Close();
                });
                dialog.Buttons([okButton, cancelButton]);
                dialog.Template(result.Template);
                dialog.Width(600);
                dialog.Open();
            });
        });
    }

    /**
     * Checks if the tab isn't saved and performs a save and refresh if needed.
     */
    protected CheckAndSave() {
        let def = $.Deferred();

        if (this.TabPage.DirtyFlag() && (this.SaveAllowed == null || this.SaveAllowed())) {
            window.baorequire(["Factories/baoDialog"], (BaoDialogViewModel) => {
                var questionDialog = new BaoDialogViewModel(Bao.Session.ApplicationName, Bao.Session.GetMessage(this.SaveCheckMessageKey), "question");
                this.subscribe(questionDialog.DialogResult, (result) => {
                    if (result) {
                        this.DeferredCompleteCheck().then((completeCheckResult) => {
                            if (completeCheckResult) {
                                this.Save().then(() => def.resolve());
                            }
                        });
                    }
                    else {
                        this.PerformRefresh().then(() => def.resolve());
                    }
                });
            });
        }
        else {
            def.resolve();
        }

        return def.promise();
    }
}


export default AuditDefinition;